import type React from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

/**
 * The column engine both designer pickers share — the action picker's "+" popup
 * (action-picker-popup.tsx) and the symbol / value picker's columns popup (symbol-picker-popup.tsx).
 *
 * It owns what a Finder-style column browser has to get right and nothing else: which branches are
 * open, which leaf is previewed, where the keyboard cursor is, hover intent (a branch opens only
 * once the pointer rests on it), the wheel-quiet window (rows do not hover-open while the wheel
 * turns), the row-leave cursor fall-back, and the browse-mode arrow keys. Rows, search, preview and
 * every test-id stay with the popup that renders them.
 *
 * Direction: columns open RIGHT-TO-LEFT (the strip is `row-reverse`), so "deeper" is ArrowLeft.
 */

/** Hover-intent: a branch opens only once the pointer has rested on it this long, so a diagonal
 *  sweep toward a deeper column does not open every row it crosses. */
export const PICKER_HOVER_INTENT_MS: number = 110;

/** Rows stop hover-opening while the wheel turns, and for this long after it stops. */
export const PICKER_WHEEL_QUIET_MS: number = 160;

/** How often an open popup checks whether focus has gone into the canvas iframe. Clicks inside the
 *  iframe never reach this document, so Radix cannot see them as "outside". */
export const PICKER_IFRAME_FOCUS_POLL_MS: number = 100;

/** The least a node must carry for the engine: a key unique within its tree. */
export interface IPickerColumnsNode {
  key: string;
}

export interface IPickerColumn<TNode extends IPickerColumnsNode> {
  /** The node whose children this column lists; null for the root column. */
  owner: TNode | null;
  title: string;
  items: TNode[];
}

export interface IPickerCursor {
  column: number;
  row: number;
}

export interface IUsePickerColumnsOptions<TNode extends IPickerColumnsNode> {
  /** The root column's rows. Identity is the memo key: pass a memoised array. */
  rootItems: TNode[];
  rootTitle: string;
  getChildren: (node: TNode) => TNode[];
  getTitle: (node: TNode) => string;
  /** A row that opens a child column (and has at least one child). */
  isBranch: (node: TNode) => boolean;
  /** A row whose activation picks it. Anything neither branch nor pickable is inert. */
  isPickable: (node: TNode) => boolean;
  /** Called for a pickable node the user activated (click, Enter). */
  onPick: (node: TNode) => void;
}

export interface IPickerColumnsController<TNode extends IPickerColumnsNode> {
  columns: IPickerColumn<TNode>[];
  openKeys: string[];
  setOpenKeys: React.Dispatch<React.SetStateAction<string[]>>;
  previewKey: string | null;
  setPreviewKey: React.Dispatch<React.SetStateAction<string | null>>;
  cursor: IPickerCursor | null;
  setCursor: React.Dispatch<React.SetStateAction<IPickerCursor | null>>;
  /** The scrolling strip that holds the columns; see `useRevealNewestColumn`. */
  stripRef: React.MutableRefObject<HTMLDivElement | null>;
  clearIntent: () => void;
  openBranch: (node: TNode, columnIndex: number) => void;
  /** The pointer came to rest on a browse row. */
  handleRowRest: (node: TNode) => void;
  /** The pointer left a row of `columnIndex`. */
  handleRowLeave: (columnIndex: number) => void;
  /** A browse row was clicked (or Enter was pressed on it). */
  handleRowActivate: (node: TNode) => void;
  /** Moves the keyboard cursor and opens / previews what it lands on. */
  moveCursorTo: (next: IPickerCursor) => void;
  /** The browse-mode keys (arrows, Home / End, Enter) from the search field. */
  handleBrowseKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /** A flat result list's keys (Up / Down / Enter); the cursor's row indexes `results`. */
  handleResultsKeyDown: (event: React.KeyboardEvent<HTMLInputElement>, results: TNode[]) => void;
  /** The pointer came to rest on row `row` of a flat result list. */
  handleResultRest: (node: TNode, row: number) => void;
  /** Bind to the strip's `onWheel`: rows stop hover-opening while it turns. */
  handleStripWheel: () => void;
}

export function usePickerColumns<TNode extends IPickerColumnsNode>({
  rootItems,
  rootTitle,
  getChildren,
  getTitle,
  isBranch,
  isPickable,
  onPick
}: IUsePickerColumnsOptions<TNode>): IPickerColumnsController<TNode> {
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [cursor, setCursor] = useState<IPickerCursor | null>(null);

  const stripRef: React.MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);
  const intentTimer: React.MutableRefObject<number | null> = useRef<number | null>(null);
  const lastWheelAt: React.MutableRefObject<number> = useRef<number>(0);

  const columns: IPickerColumn<TNode>[] = useMemo((): IPickerColumn<TNode>[] => {
    const list: IPickerColumn<TNode>[] = [{ owner: null, title: rootTitle, items: rootItems }];
    let items: TNode[] = rootItems;
    for (const key of openKeys) {
      const owner: TNode | undefined = items.find((node: TNode): boolean => node.key === key);
      if (!owner || !isBranch(owner) || getChildren(owner).length === 0) {
        break;
      }
      list.push({ owner, title: getTitle(owner), items: getChildren(owner) });
      items = getChildren(owner);
    }
    return list;
    // The node predicates are pure functions of the node; the tree's identity is the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootTitle, rootItems, openKeys]);

  useEffect(
    (): (() => void) => (): void => {
      if (intentTimer.current !== null) {
        window.clearTimeout(intentTimer.current);
      }
    },
    []
  );

  const clearIntent = useCallback((): void => {
    if (intentTimer.current !== null) {
      window.clearTimeout(intentTimer.current);
      intentTimer.current = null;
    }
  }, []);

  const columnIndexOf = useCallback(
    (node: TNode): number => columns.findIndex((column: IPickerColumn<TNode>): boolean => column.items.includes(node)),
    [columns]
  );

  const openBranch = useCallback((node: TNode, columnIndex: number): void => {
    setOpenKeys((keys: string[]): string[] => [...keys.slice(0, columnIndex), node.key]);
    setPreviewKey(null);
  }, []);

  const handleRowRest = useCallback(
    (node: TNode): void => {
      const columnIndex: number = columnIndexOf(node);
      if (columnIndex < 0) {
        return;
      }
      const row: number = columns[columnIndex].items.indexOf(node);
      setCursor({ column: columnIndex, row });
      clearIntent();
      if (!isBranch(node)) {
        setOpenKeys((keys: string[]): string[] => keys.slice(0, columnIndex));
        setPreviewKey(isPickable(node) ? node.key : null);
        return;
      }
      if (Date.now() - lastWheelAt.current < PICKER_WHEEL_QUIET_MS) {
        return;
      }
      intentTimer.current = window.setTimeout((): void => openBranch(node, columnIndex), PICKER_HOVER_INTENT_MS);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnIndexOf, columns, clearIntent, openBranch]
  );

  const handleRowActivate = useCallback(
    (node: TNode): void => {
      clearIntent();
      if (isPickable(node)) {
        onPick(node);
        return;
      }
      if (isBranch(node)) {
        const columnIndex: number = columnIndexOf(node);
        if (columnIndex >= 0) {
          openBranch(node, columnIndex);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearIntent, onPick, columnIndexOf, openBranch]
  );

  /**
   * The pointer left a row. Resting on a row moved the cursor there and armed its branch; leaving —
   * onto a sibling, which rests at once, or out of the popup — must not leave that row lit beside the
   * branch that is actually open. The cursor falls back to the column's open (or previewed) row.
   */
  const handleRowLeave = useCallback(
    (columnIndex: number): void => {
      clearIntent();
      setCursor((current: IPickerCursor | null): IPickerCursor | null => {
        if (!current || current.column !== columnIndex) {
          return current;
        }
        const openRow: number = (columns[columnIndex]?.items ?? []).findIndex(
          (node: TNode): boolean => node.key === openKeys[columnIndex] || node.key === previewKey
        );
        return openRow >= 0 ? { column: columnIndex, row: openRow } : null;
      });
    },
    [clearIntent, columns, openKeys, previewKey]
  );

  const handleResultRest = useCallback((node: TNode, row: number): void => {
    setPreviewKey(node.key);
    setCursor({ column: 0, row });
  }, []);

  const moveCursorTo = (next: IPickerCursor): void => {
    setCursor(next);
    const column: IPickerColumn<TNode> | undefined = columns[next.column];
    const node: TNode | undefined = column?.items[next.row];
    if (!node) {
      return;
    }
    if (isBranch(node)) {
      openBranch(node, next.column);
    } else {
      setOpenKeys((keys: string[]): string[] => keys.slice(0, next.column));
      setPreviewKey(isPickable(node) ? node.key : null);
    }
  };

  const handleResultsKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, results: TNode[]): void => {
    const count: number = results.length;
    const row: number = cursor?.row ?? 0;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (count === 0) {
        return;
      }
      const next: number = event.key === "ArrowDown" ? Math.min(count - 1, row + 1) : Math.max(0, row - 1);
      setCursor({ column: 0, row: next });
      setPreviewKey(results[next].key);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const node: TNode | undefined = results[row];
      if (node) {
        onPick(node);
      }
    }
  };

  const handleBrowseKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    const current: IPickerCursor = cursor ?? { column: columns.length - 1, row: -1 };
    const column: IPickerColumn<TNode> = columns[current.column] ?? columns[0];
    const node: TNode | undefined = column.items[current.row];
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault();
        if (column.items.length === 0) {
          return;
        }
        const delta: number = event.key === "ArrowDown" ? 1 : -1;
        const next: number = Math.max(0, Math.min(column.items.length - 1, current.row + delta));
        moveCursorTo({ column: current.column, row: next });
        return;
      }
      case "Home":
      case "End": {
        event.preventDefault();
        const next: number = event.key === "Home" ? 0 : column.items.length - 1;
        moveCursorTo({ column: current.column, row: next });
        return;
      }
      // Deeper is LEFT: that is where the child column opens.
      case "ArrowLeft": {
        if (!node || !isBranch(node)) {
          return;
        }
        event.preventDefault();
        openBranch(node, current.column);
        const child: TNode | undefined = getChildren(node)[0];
        setCursor({ column: current.column + 1, row: 0 });
        if (child && isPickable(child)) {
          setPreviewKey(child.key);
        }
        return;
      }
      case "ArrowRight": {
        if (current.column === 0) {
          return;
        }
        event.preventDefault();
        const parentColumn: IPickerColumn<TNode> = columns[current.column - 1];
        const owner: TNode | null = columns[current.column].owner;
        const row: number = owner ? parentColumn.items.indexOf(owner) : 0;
        setOpenKeys((keys: string[]): string[] => keys.slice(0, current.column));
        setCursor({ column: current.column - 1, row: Math.max(0, row) });
        setPreviewKey(null);
        return;
      }
      case "Enter": {
        event.preventDefault();
        if (node) {
          handleRowActivate(node);
          if (isBranch(node)) {
            setCursor({ column: current.column + 1, row: 0 });
          }
        }
        return;
      }
      default:
        return;
    }
  };

  const handleStripWheel = useCallback((): void => {
    lastWheelAt.current = Date.now();
    clearIntent();
  }, [clearIntent]);

  return {
    columns,
    openKeys,
    setOpenKeys,
    previewKey,
    setPreviewKey,
    cursor,
    setCursor,
    stripRef,
    clearIntent,
    openBranch,
    handleRowRest,
    handleRowLeave,
    handleRowActivate,
    moveCursorTo,
    handleBrowseKeyDown,
    handleResultsKeyDown,
    handleResultRest,
    handleStripWheel
  };
}

/**
 * A newly opened column appears at the strip's LEFT end (row-reverse). When the strip is already as
 * wide as the window allows, bring that end into view; when it is not, there is nothing to scroll.
 *
 * Keyed on the open PATH, not the column count: opening a sibling branch at the same depth swaps in a
 * column of a different width without changing the count, and that wider column was left past the
 * strip's left edge (KOA-18648 QA, Custom → datasource → folder).
 */
export function useRevealNewestColumn(
  stripRef: React.MutableRefObject<HTMLDivElement | null>,
  openKeys: readonly string[],
  isPreviewShown: boolean
): void {
  const openPath: string = openKeys.join("\n");
  useLayoutEffect((): void => {
    const strip: HTMLDivElement | null = stripRef.current;
    if (strip && strip.scrollWidth > strip.clientWidth) {
      strip.scrollLeft = -strip.scrollWidth;
    }
  }, [stripRef, openPath, isPreviewShown]);
}

/** Focus into the canvas iframe closes the popup — the canvas swallows the click Radix would see. */
export function useCloseOnIframeFocus(open: boolean, onOpenChange: (open: boolean) => void): void {
  useEffect((): (() => void) | undefined => {
    if (!open) {
      return undefined;
    }
    const timer: number = window.setInterval((): void => {
      if (document.activeElement?.tagName === "IFRAME") {
        onOpenChange(false);
      }
    }, PICKER_IFRAME_FOCUS_POLL_MS);
    return (): void => window.clearInterval(timer);
  }, [open, onOpenChange]);
}

/** Keeps focus in the search field: a row is operated by pointer or by the field's arrow keys. A row
 *  that took focus would leave Backspace to the designer's shortcut listener, which deletes the
 *  selected component on it. */
export const keepPickerFieldFocus = (event: React.MouseEvent): void => event.preventDefault();

/**
 * Keys pressed in the popup stay in the popup. It is portalled to <body>, so without this a key
 * would still reach the designer's window-level shortcuts (Delete / Backspace remove the selected
 * component, Ctrl+Z undoes the canvas) whenever focus is not in a text field. Bind it on the Radix
 * Content itself — the focusable element (tabIndex -1) a click on non-focusable popup content lands
 * on. Escape passes: Radix's own dismiss listener is on the document.
 */
export const stopPickerKeyPropagation = (event: React.KeyboardEvent<HTMLDivElement>): void => {
  if (event.key !== "Escape") {
    event.stopPropagation();
  }
};

/**
 * …and focus does not leave the search field in the first place: a press anywhere but a field or a
 * button (a column title, the preview text, the footer, padding) keeps it there, so the field's
 * keys keep driving the picker and Backspace keeps editing the query.
 */
export const keepPickerFocusInField = (event: React.MouseEvent<HTMLDivElement>): void => {
  const target: Element | null = event.target instanceof Element ? event.target : null;
  if (target && !target.closest("input, textarea, button, a[href], [role='radiogroup']")) {
    event.preventDefault();
  }
};
